import React, { useCallback, useEffect, useState } from "react";

export type Locale = "en" | "fr";

const STORAGE_KEY = "negopoly.locale";
const LOCALE_EVENT = "negopoly:locale-change";

export function getLocale(): Locale {
  if (typeof window === "undefined") return "en";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "en" || stored === "fr") return stored;
  } catch {
    // Fall back to the browser locale when storage is unavailable.
  }
  const browserLocale = (navigator.languages?.[0] ?? navigator.language ?? "en").toLowerCase();
  return browserLocale.startsWith("fr") ? "fr" : "en";
}

export function setLocale(locale: Locale) {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // The current page can still switch language without persistence.
  }
  document.documentElement.lang = locale;
  window.dispatchEvent(new CustomEvent<Locale>(LOCALE_EVENT, { detail: locale }));
}

export function useLocale() {
  const [locale, setCurrentLocale] = useState<Locale>(getLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
    const onLocaleChange = (event: Event) => {
      setCurrentLocale((event as CustomEvent<Locale>).detail);
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY && (event.newValue === "en" || event.newValue === "fr")) {
        setCurrentLocale(event.newValue);
      }
    };
    window.addEventListener(LOCALE_EVENT, onLocaleChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(LOCALE_EVENT, onLocaleChange);
      window.removeEventListener("storage", onStorage);
    };
  }, [locale]);

  const changeLocale = useCallback((nextLocale: Locale) => {
    setCurrentLocale(nextLocale);
    setLocale(nextLocale);
  }, []);

  return { locale, setLocale: changeLocale };
}

export function LanguageSwitcher({
  className = "",
  compact = false,
}: {
  className?: string;
  compact?: boolean;
}) {
  const { locale, setLocale: changeLocale } = useLocale();
  const label = locale === "fr" ? "Choisir la langue" : "Choose language";

  return (
    <div className={`language-switcher ${compact ? "compact" : ""} ${className}`.trim()} role="group" aria-label={label}>
      <button type="button" className={locale === "en" ? "active" : ""} onClick={() => changeLocale("en")} aria-pressed={locale === "en"} lang="en">
        {compact ? "EN" : "English"}
      </button>
      <button type="button" className={locale === "fr" ? "active" : ""} onClick={() => changeLocale("fr")} aria-pressed={locale === "fr"} lang="fr">
        {compact ? "FR" : "Français"}
      </button>
    </div>
  );
}
